import { useState, useEffect } from 'react';
import { connect } from 'react-redux'
import * as Actions from '../../actions/actions';
import Button from '@mui/material/Button';
import { DataGrid } from '@mui/x-data-grid';
import Switch from '@mui/material/Switch';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import axios from 'axios';

const baseURL = process.env.REACT_APP_API_URL;
const adminSupportURL = `${baseURL}/VorAdminSupportPortal`;

// Define all toggleable DC properties here.
// Adding a new property only requires adding an entry to this array.
const DC_PROPERTIES = [
    { key: "is_image_correction_enabled", label: "IMAGE CORRECTION" },
    { key: "use_route_import_for_stops", label: "ROUTE IMPORT FOR STOPS" },
];

const LocationManagementGridPanel = ({ user, ...props }) => {
    const [locationsList, setLocationsList] = useState(props.locationsList);
    // dcPropertiesMap: { [central_location_id]: { dc_id, is_image_correction_enabled, use_route_import_for_stops, ... } }
    const [dcPropertiesMap, setDcPropertiesMap] = useState({});
    const [togglingKey, setTogglingKey] = useState(null); // "dc_id:property_name" while toggling
    const [notification, setNotification] = useState({ show: false, type: 'success', message: '' });

    const handleEditLocationTapped = (params) => {
        props.showAddEditLocationModal('Edit', true, params);
    }

    useEffect(() => {
        let filteredLocations = props.locationsList.filter((item) => {
            if (item.central_customer_id == props.centralCustomerId) {
                return item;
            }
        })
        setLocationsList(filteredLocations);
    }, [props.locationsList, props.centralCustomerId]);

    // Load DC properties from customer_dcs API
    useEffect(() => {
        const loadDcProperties = async () => {
            try {
                const response = await axios.get(`${adminSupportURL}/customer_dcs`, {
                    headers: { "Authorization": localStorage.getItem("authToken") }
                });
                const data = response.data.Data || [];
                const propMap = {};
                data.forEach(customer => {
                    (customer.dcs || []).forEach(dc => {
                        if (dc.central_location_id != null) {
                            propMap[dc.central_location_id] = {
                                dc_id: dc.dc_id,
                                is_image_correction_enabled: Boolean(dc.is_image_correction_enabled),
                                use_route_import_for_stops: Boolean(dc.use_route_import_for_stops),
                            };
                        }
                    });
                });
                setDcPropertiesMap(propMap);
            } catch (err) {
                console.error("Failed to load DC properties:", err);
            }
        };
        loadDcProperties();
    }, []);

    const handleToggleProperty = async (vorDcId, propertyName, currentValue) => {
        const toggleKey = `${vorDcId}:${propertyName}`;
        setTogglingKey(toggleKey);
        try {
            await axios.put(`${adminSupportURL}/customer_dcs`,
                JSON.stringify({ dc_id: vorDcId, property_name: propertyName, enabled: !currentValue }),
                {
                    headers: {
                        "Authorization": localStorage.getItem("authToken"),
                        "Content-Type": "application/json"
                    }
                }
            );
            // Update local state
            setDcPropertiesMap(prev => {
                const updated = { ...prev };
                const entry = Object.entries(updated).find(([_, v]) => v.dc_id === vorDcId);
                if (entry) {
                    updated[entry[0]] = { ...entry[1], [propertyName]: !currentValue };
                }
                return updated;
            });
            const label = DC_PROPERTIES.find(p => p.key === propertyName)?.label || propertyName;
            setNotification({
                show: true,
                type: 'success',
                message: `${label} ${!currentValue ? 'enabled' : 'disabled'} successfully.`
            });
        } catch (err) {
            console.error(`Failed to toggle ${propertyName}:`, err);
            setNotification({
                show: true,
                type: 'error',
                message: `Failed to update ${propertyName}.`
            });
        } finally {
            setTogglingKey(null);
        }
    };

    const editLocationButton = (params) => {
        if (params.row) {
            return <Button variant="contained" onClick={() => handleEditLocationTapped(params.row)}>Edit Location</Button>
        } else {
            return null
        }
    }
    const customerStatus = (params) => {
        if (params.row.active) {
            return "Active"
        } else {
            return "Inactive"
        }
    }

    const getAddress = (params) => {
        if (params.row.address && params.row.address.full_address) {
            return params.row.address.full_address;
        } else {
            return ""
        }
    }
    const getLatitude = (params) => {
        if (params.row.address && params.row.address.point_lat) {
            return params.row.address.point_lat;
        } else {
            return ""
        }
    }
    const getLongitude = (params) => {
        if (params.row.address && params.row.address.point_long) {
            return params.row.address.point_long;
        } else {
            return ""
        }
    }

    // Generic toggle renderer for any DC property
    const renderPropertyToggle = (params, propertyName) => {
        const centralLocationId = params.row.central_location_id;
        const mapEntry = dcPropertiesMap[centralLocationId];
        if (!mapEntry || mapEntry.dc_id == null) return <span style={{ color: '#999' }}>N/A</span>;
        const isEnabled = mapEntry[propertyName] === true;
        const vorDcId = mapEntry.dc_id;
        const toggleKey = `${vorDcId}:${propertyName}`;
        const isToggling = togglingKey === toggleKey;
        return (
            <Switch
                checked={isEnabled}
                onChange={() => handleToggleProperty(vorDcId, propertyName, isEnabled)}
                color="primary"
                disabled={isToggling}
            />
        );
    };

    // Build toggle columns dynamically from DC_PROPERTIES
    const propertyToggleColumns = DC_PROPERTIES.map(prop => ({
        field: `toggle_${prop.key}`,
        headerName: prop.label,
        width: 200,
        renderCell: (params) => renderPropertyToggle(params, prop.key),
    }));

    const columns = [
        { field: 'central_location_id', headerName: 'CENTRAL LOCATION ID', width: 180 },
        { field: 'location_name', headerName: 'LOCATION NAME', width: 140 },
        { field: 'active', headerName: 'STATUS', width: 90, renderCell: customerStatus },
        { field: 'address', headerName: 'ADDRESS', width: 270, renderCell: getAddress },
        { field: 'latitude', headerName: 'LATITUDE', width: 100, renderCell: getLatitude },
        { field: 'longitude', headerName: 'LONGITUDE', width: 100, renderCell: getLongitude },
        { field: 'time_zone', headerName: 'TIME ZONE', width: 120 },
        { field: 'warehouse_id', headerName: 'WAREHOUSE ID', width: 140 },
        { field: 'email', headerName: 'EMAIL', width: 170 },
        { field: 'geo_fence_radius', headerName: 'GEO FENCE RADIUS (m)', width: 180 },
        ...propertyToggleColumns,
        user.canWrite && { field: 'editLocation', headerName: 'EDIT LOCATION', width: 170, renderCell: editLocationButton }
    ];

    return (
        <>
            <div className="dashboardData-panel">
                <div className="duolift-device-detail-card" style={{ width: "100%" }}>
                    <div className="detail-card-header">
                        <h3 style={{ paddingTop: '10px' }}>Locations</h3>
                    </div>
                    <div className="duolift-device-detail-card-body">
                        <div style={{ height: '650px', width: '100%' }}>
                            <DataGrid
                                rows={locationsList}
                                columns={columns}
                                rowsPerPageOptions={[10]}
                                getRowId={(row) => row.central_location_id}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <Snackbar
                open={notification.show}
                autoHideDuration={4000}
                onClose={() => setNotification({ ...notification, show: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setNotification({ ...notification, show: false })}
                    severity={notification.type}
                    sx={{ width: '100%' }}
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </>
    );
}

const mapStateToProps = (state) => {
    return {
        showModal: state.customerManagement.showCustomerModal,
        showModalType: state.customerManagement.showModalType,
        user: state.application.user
    };
}
const mapDispatchToProps = (dispatch) => {
    return {
        showAddEditLocationModal: (type, show, selectedData) => dispatch(Actions.LOCATION_ADD_EDIT_MODAL(type, show, selectedData))
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(LocationManagementGridPanel);
